"use client"

import { useState } from "react"

export function WebsiteDevelopment() {
  const [lightboxIndex, setLightboxIndex] = useState(null)

  const websiteProjects = [
    {
      src: "/placeholder.svg?height=300&width=400",
      title: "Modern E-commerce Website",
      category: "E-commerce",
      description: "A fully responsive online store with seamless shopping experience and secure payment integration.",
      link: "#",
      features: ["Responsive Design", "Secure Payment Gateway", "Inventory Management"],
      technologies: ["React", "Node.js", "MongoDB"],
      price: 3000,
      completionTime: "8 weeks",
      lastUpdated: "2024-03-15",
    },
    {
      src: "/placeholder.svg?height=300&width=400",
      title: "Corporate Business Website",
      category: "Business",
      description: "Professional website showcasing company services, team, and achievements with modern design.",
      link: "#",
      features: ["About Us Page", "Services Section", "Contact Form"],
      technologies: ["HTML", "CSS", "JavaScript"],
      price: 1500,
      completionTime: "6 weeks",
      lastUpdated: "2024-02-20",
    },
    {
      src: "/placeholder.svg?height=300&width=400",
      title: "Restaurant Website",
      category: "Food & Beverage",
      description: "Interactive restaurant website featuring online ordering and table reservations.",
      link: "#",
      features: ["Online Ordering", "Table Reservations", "Menu Management"],
      technologies: ["React", "Node.js", "MySQL"],
      price: 2000,
      completionTime: "10 weeks",
      lastUpdated: "2024-04-10",
    },
    {
      src: "/placeholder.svg?height=300&width=400",
      title: "Real Estate Platform",
      category: "Real Estate",
      description: "Property listing website with advanced search and virtual tour features.",
      link: "#",
      features: ["Property Search", "Virtual Tours", "Map Integration"],
      technologies: ["React", "Node.js", "PostgreSQL"],
      price: 4000,
      completionTime: "12 weeks",
      lastUpdated: "2024-05-01",
    },
    {
      src: "/placeholder.svg?height=300&width=400",
      title: "Educational Institute Website",
      category: "Education",
      description: "Comprehensive website for educational institution with course management and student portal.",
      link: "#",
      features: ["Course Catalog", "Student Portal", "Admin Panel"],
      technologies: ["React", "Django", "PostgreSQL"],
      price: 3500,
      completionTime: "10 weeks",
      lastUpdated: "2024-04-25",
    },
    {
      src: "/placeholder.svg?height=300&width=400",
      title: "Artist Portfolio",
      category: "Portfolio",
      description: "Creative portfolio website showcasing artistic works with elegant gallery features.",
      link: "#",
      features: ["Image Gallery", "Bio Section", "Contact Form"],
      technologies: ["React", "Next.js", "MongoDB"],
      price: 1000,
      completionTime: "4 weeks",
      lastUpdated: "2024-01-15",
    },
  ]

  const developmentProcess = [
    {
      step: 1,
      title: "Discovery & Planning",
      description: "Understanding your vision and planning the perfect website strategy.",
      activities: [
        "Initial Consultation",
        "Website Goals Analysis",
        "Target Audience Research",
        "Content Strategy Planning",
        "Technical Requirements",
        "Project Timeline Creation",
      ],
      duration: "1 week",
    },
    {
      step: 2,
      title: "Design Phase",
      description: "Creating the visual identity and user experience of your website.",
      activities: [
        "Wireframe Creation",
        "UI/UX Design",
        "Color Scheme Selection",
        "Typography Selection",
        "Mobile Design Layout",
        "Design Review & Feedback",
      ],
      duration: "1-2 weeks",
    },
    {
      step: 3,
      title: "Development",
      description: "Building your website with clean, efficient, and maintainable code.",
      activities: [
        "Frontend Development",
        "Backend Integration",
        "CMS Implementation",
        "Responsive Development",
        "Performance Optimization",
        "SEO Implementation",
      ],
      duration: "2-4 weeks",
    },
    {
      step: 4,
      title: "Content & Testing",
      description: "Populating content and ensuring everything works perfectly.",
      activities: [
        "Content Upload",
        "Cross-browser Testing",
        "Mobile Responsiveness Check",
        "Performance Testing",
        "Security Testing",
        "User Testing",
      ],
      duration: "1 week",
    },
    {
      step: 5,
      title: "Launch",
      description: "Deploying your website and ensuring smooth operation.",
      activities: [
        "Domain Setup",
        "SSL Installation",
        "Server Configuration",
        "Analytics Setup",
        "Final Testing",
        "Go Live",
      ],
      duration: "2-3 days",
    },
    {
      step: 6,
      title: "Post-Launch Support",
      description: "Ongoing maintenance and support for your website.",
      activities: [
        "Performance Monitoring",
        "Security Updates",
        "Content Updates",
        "Technical Support",
        "Analytics Reporting",
        "Regular Backups",
      ],
      duration: "Ongoing",
    },
  ]

  const benefits = [
    {
      title: "Responsive Design",
      description: "Websites that look and work perfectly on all devices",
    },
    {
      title: "SEO Optimized",
      description: "Built with search engine visibility in mind",
    },
    {
      title: "Fast Loading",
      description: "Optimized performance for better user experience",
    },
    {
      title: "Easy to Manage",
      description: "User-friendly content management system",
    },
    {
      title: "Secure & Reliable",
      description: "Built with latest security standards",
    },
    {
      title: "24/7 Support",
      description: "Dedicated support team for your website",
    },
  ]

  const packages = [
    {
      name: "Basic Website Package",
      price: 999,
      description: "Perfect for small businesses and personal websites",
      features: [
        "5 Custom Pages",
        "Mobile Responsive Design",
        "Contact Form",
        "Basic SEO Setup",
        "Social Media Integration",
        "Google Analytics",
        "Content Management System",
        "3 Months Support",
      ],
    },
    {
      name: "Professional Website Package",
      price: 2499,
      description: "Ideal for growing businesses and e-commerce",
      features: [
        "Unlimited Custom Pages",
        "E-commerce Integration",
        "Advanced SEO Package",
        "Custom Animations",
        "Blog Setup",
        "Newsletter Integration",
        "Payment Gateway Setup",
        "Priority Support",
        "6 Months Support",
        "Performance Optimization",
      ],
    },
  ]

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 pt-5 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="lg:grid lg:grid-cols-2 lg:gap-16 font-poppins items-center mb-24">
          <div className="mb-12 lg:mb-0 space-y-8">
            <div className="space-y-6">
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl xl:text-6xl/none">
                Custom Website
                <span className="block mt-2">
                  Development <span className="text-red-600">Services</span>
                </span>
              </h1>
              <p className="text-lg text-gray-600 md:text-xl">
                Transform your online presence with our custom website development services. We create stunning,
                high-performance websites that drive results.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium flex items-center transition-colors">
                  Get Started
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 ml-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                <button className="px-6 py-3 border border-gray-300 hover:border-gray-400 rounded-lg font-medium flex items-center transition-colors">
                  View Portfolio
                </button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="p-4 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-shadow duration-200"
                >
                  <div className="flex items-start space-x-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-red-500 flex-shrink-0 mt-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div>
                      <h3 className="font-medium text-gray-900">{benefit.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">{benefit.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="relative rounded-2xl shadow-2xl overflow-hidden bg-gray-900 aspect-video group">
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900/40 to-black/60 z-10" />
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                title="Website Development Process"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
              <div className="absolute inset-0 z-20 flex items-center justify-center">
                <div className="rounded-full bg-red-600 p-4 transform transition-transform duration-200 group-hover:scale-110">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-white"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Website Portfolio */}
        <div className="mb-24 font-poppins">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Website Portfolio</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore our recent website projects and see how we help businesses establish their online presence.
            </p>
          </div>
          <div className="space-y-8">
            {websiteProjects.map((project, index) => (
              <div
                key={index}
                className="flex flex-col md:flex-row bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className="md:w-1/3 relative overflow-hidden">
                  <img
                    src={project.src || "/placeholder.svg"}
                    alt={project.title}
                    className="w-full h-48 md:h-full object-cover transform hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-red-600 text-white text-sm rounded-full">{project.category}</span>
                  </div>
                </div>
                <div className="md:w-2/3 p-6 md:p-8">
                  <div className="space-y-4">
                    <div className="flex justify-between items-start">
                      <h3 className="text-2xl font-bold hover:text-red-600 transition-colors">{project.title}</h3>
                      <div className="text-lg font-semibold text-red-600">
                        Starting at ${project.price.toLocaleString()}
                      </div>
                    </div>

                    <p className="text-gray-600">{project.description}</p>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Key Features</h4>
                        <ul className="space-y-2">
                          {project.features.map((feature, i) => (
                            <li key={i} className="flex items-center text-gray-600 text-sm">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 text-green-500 mr-2 flex-shrink-0"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Technologies Used</h4>
                        <div className="flex flex-wrap gap-2">
                          {project.technologies.map((tech, i) => (
                            <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 text-sm rounded-md">
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-500">
                          Completion time: {project.completionTime || "4-6 weeks"}
                        </span>
                        <span className="text-sm text-gray-500">Last updated: {project.lastUpdated || "2024"}</span>
                      </div>
                      <a
                        href={project.link}
                        className="inline-flex items-center text-red-600 hover:text-red-700 font-medium"
                      >
                        View Website
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 ml-2"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Development Process */}
        <div className="mb-24 font-poppins">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Website Development Process</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A streamlined approach to bring your website from concept to reality.
            </p>
          </div>
          <div className="grid gap-8">
            {developmentProcess.map((phase, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 relative">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-red-100 text-red-600 text-xl font-bold">
                    {phase.step}
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-xl font-bold mb-2">{phase.title}</h3>
                    <p className="text-gray-600 mb-4">{phase.description}</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-semibold mb-2">Activities:</h4>
                        <ul className="list-disc list-inside text-gray-600 space-y-1">
                          {phase.activities.map((activity, i) => (
                            <li key={i}>{activity}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">Estimated Duration:</h4>
                        <p className="text-gray-600">{phase.duration}</p>
                      </div>
                    </div>
                  </div>
                </div>
                {index < developmentProcess.length - 1 && (
                  <div className="hidden md:block absolute left-6 bottom-0 w-0.5 h-8 bg-red-200 transform translate-y-full"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Section */}
        <div>
          <div className="text-center mb-12 font-poppins">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Website Development Packages</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the perfect package for your website needs.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {packages.map((pkg, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl shadow-lg overflow-hidden ${index === 1 ? "border-2 border-red-500" : ""}`}
              >
                {index === 1 && (
                  <div className="bg-red-500 text-white text-center py-2 text-sm font-medium">MOST POPULAR</div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                  <p className="text-gray-600 mb-4">{pkg.description}</p>
                  <div className="flex items-baseline mb-8">
                    <span className="text-4xl font-bold">${pkg.price}</span>
                    <span className="text-gray-500 ml-2">USD</span>
                  </div>
                  <ul className="space-y-4 mb-8">
                    {pkg.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-green-500 mr-2"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    className={`w-full inline-flex items-center justify-center px-6 py-3 rounded-full text-white ${
                      index === 1 ? "bg-red-500 hover:bg-red-600" : "bg-gray-800 hover:bg-gray-900"
                    } transition-colors duration-300`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Get Started
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setLightboxIndex(null)}
        >
          <div
            className="bg-white rounded-xl max-w-4xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-bold">{websiteProjects[lightboxIndex].title}</h3>
                <button className="p-2 hover:bg-gray-100 rounded-full" onClick={() => setLightboxIndex(null)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <img
                src={websiteProjects[lightboxIndex].src || "/placeholder.svg"}
                alt={websiteProjects[lightboxIndex].title}
                className="w-full h-auto rounded-lg mb-6"
              />
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold mb-2">Description</h4>
                  <p className="text-gray-600">{websiteProjects[lightboxIndex].description}</p>
                </div>
                <div>
                  <h4 className="text-lg font-semibold mb-2">Key Features</h4>
                  <ul className="grid grid-cols-2 gap-2">
                    {websiteProjects[lightboxIndex].features &&
                      websiteProjects[lightboxIndex].features.map((feature, i) => (
                        <li key={i} className="flex items-center text-gray-600">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-green-500 mr-2"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          {feature}
                        </li>
                      ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold mb-2">Technologies Used</h4>
                  <div className="flex flex-wrap gap-2">
                    {websiteProjects[lightboxIndex].technologies &&
                      websiteProjects[lightboxIndex].technologies.map((tech, i) => (
                        <span key={i} className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700">
                          {tech}
                        </span>
                      ))}
                  </div>
                </div>
                <div className="flex justify-between items-center pt-4 border-t">
                  <span className="text-xl font-bold text-gray-900">{websiteProjects[lightboxIndex].price}</span>
                  <button className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                    Get Started
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

